<h2 mat-dialog-title>
  Document Center
  @if (center(); as c) {
    <span class="subtitle">{{ c.employeeName }} · {{ c.employeeNumber }}</span>
  }
</h2>

<mat-dialog-content class="docs-dialog">
  @if (loading()) {
    <div class="loading"><mat-spinner diameter="40"></mat-spinner></div>
  } @else if (error() && !center()) {
    <p class="error">{{ error() }}</p>
  } @else {
    @if (center(); as c) {
    @if (error()) { <p class="error">{{ error() }}</p> }

    @if (canWrite) {
      <section class="upload-panel">
        <h3>Upload document</h3>
        <div class="upload-grid">
          <mat-form-field appearance="outline">
            <mat-label>Document type</mat-label>
            <mat-select [(ngModel)]="uploadTypeId">
              @for (t of typeOptions(); track t.id) {
                <mat-option [value]="t.id">{{ t.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Custom title (optional)</mat-label>
            <input matInput [(ngModel)]="uploadCustomTitle" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>{{ selectedTypeRequiresExpiry() ? 'Expiry date (required)' : 'Expiry date' }}</mat-label>
            <input matInput type="date" [(ngModel)]="uploadExpiry" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Remarks</mat-label>
            <input matInput [(ngModel)]="uploadRemarks" />
          </mat-form-field>
        </div>
        <div class="upload-actions">
          <input #fileInput type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,application/pdf,image/jpeg,image/png,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" (change)="onFilePicked($event)" />
          <button mat-stroked-button type="button" (click)="fileInput.click()">Choose file</button>
          <span class="file-name">{{ selectedFile?.name || 'No file selected' }}</span>
          <button mat-raised-button color="primary" type="button" [disabled]="uploading() || !selectedFile || !uploadTypeId" (click)="upload()">
            {{ uploading() ? 'Uploading…' : 'Upload' }}
          </button>
        </div>
        <p class="hint">PDF, JPG, PNG, DOC, DOCX · max {{ formatSize(c.maxFileSizeBytes) }}</p>
      </section>
    }

    <mat-tab-group>
      @for (cat of c.categories; track cat.category) {
        <mat-tab [label]="cat.categoryLabel">
          <div class="category-body">
            @for (slot of cat.slots; track slot.documentTypeId) {
              <div class="slot">
                <div class="slot-head">
                  <h4>{{ slot.typeName }}</h4>
                  <span class="muted">{{ slot.documents.length }} file(s){{ slot.allowsMultiple ? ' · multiple allowed' : '' }}</span>
                </div>

                @if (!slot.documents.length) {
                  <p class="empty">No document uploaded.</p>
                } @else {
                  @for (doc of slot.documents; track doc.id) {
                    <div class="doc-card">
                      <div class="doc-main">
                        <div class="doc-title">{{ doc.displayName }}</div>
                        <div class="doc-meta">
                          <span>{{ doc.fileName || '—' }}</span>
                          <span>{{ formatSize(doc.fileSizeBytes) }}</span>
                          <span>v{{ doc.currentVersionNumber }}</span>
                          <span>{{ doc.statusLabel }}</span>
                          @if (doc.isVerified) { <span class="verified">Verified</span> }
                        </div>
                        <div class="doc-meta muted">
                          Uploaded by {{ doc.uploadedByName || '—' }} on {{ doc.uploadedAt | date:'medium' }}
                          @if (doc.expiryDate) { · Expires {{ doc.expiryDate }} }
                          @if (doc.remarks) { · {{ doc.remarks }} }
                        </div>
                      </div>
                      <div class="doc-actions">
                        <button mat-icon-button matTooltip="Preview" type="button" (click)="preview(doc)" [disabled]="!doc.canPreview && !doc.downloadUrl">
                          <mat-icon>visibility</mat-icon>
                        </button>
                        <button mat-icon-button matTooltip="Download" type="button" (click)="download(doc)">
                          <mat-icon>download</mat-icon>
                        </button>
                        <button mat-icon-button matTooltip="Version history" type="button" (click)="openVersions(doc)">
                          <mat-icon>history</mat-icon>
                        </button>
                        @if (canWrite) {
                          <button mat-icon-button matTooltip="Replace" type="button" (click)="replaceInput.click()">
                            <mat-icon>upload_file</mat-icon>
                          </button>
                          <input #replaceInput type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" (change)="replace(doc, $event)" />
                          <button mat-icon-button matTooltip="Delete" color="warn" type="button" (click)="deleteDoc(doc)">
                            <mat-icon>delete</mat-icon>
                          </button>
                        }
                        @if (canVerify) {
                          @if (!doc.isVerified) {
                            <button mat-stroked-button color="primary" type="button" (click)="verify(doc, true)">
                              Verify
                            </button>
                            <button mat-stroked-button color="warn" type="button" (click)="verify(doc, false, true)">
                              Reject
                            </button>
                          } @else {
                            <button mat-stroked-button type="button" (click)="verify(doc, false, false)">
                              Unverify
                            </button>
                          }
                        }
                      </div>

                      @if (showVersionsFor() === doc.id) {
                        <div class="versions">
                          <h5>Version history</h5>
                          @for (v of versions(); track v.id) {
                            <div class="version-row">
                              <span>v{{ v.versionNumber }}{{ v.isCurrent ? ' (current)' : '' }}</span>
                              <span>{{ v.originalFileName }}</span>
                              <span>{{ formatSize(v.sizeBytes) }}</span>
                              <span>{{ v.uploadedByName }} · {{ v.uploadedAt | date:'short' }}</span>
                              <button mat-button type="button" (click)="downloadVersion(doc.id, v)">Download</button>
                            </div>
                          }
                        </div>
                      }
                    </div>
                  }
                }
              </div>
            }
          </div>
        </mat-tab>
      }
    </mat-tab-group>

    @if (previewUrl()) {
      <section class="preview-panel">
        <div class="preview-head">
          <strong>Preview · {{ previewName() }}</strong>
          <button mat-button type="button" (click)="clearPreview()">Close preview</button>
        </div>
        @if (previewIsPdf()) {
          <iframe [src]="previewUrl()" title="Document preview"></iframe>
        } @else {
          <img [src]="previewRawUrl()" alt="Document preview" />
        }
      </section>
    }
    }
  }
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="reload()">Refresh</button>
  <button mat-raised-button color="primary" type="button" (click)="close()">Close</button>
</mat-dialog-actions>
